import { z } from "zod";

export const SCHEDULE_TYPES = ["visit", "inperson", "call", "fu"] as const;

export const SCHEDULE_TYPE_LABELS: Record<string, string> = {
	visit: "외래",
	inperson: "대면",
	call: "전화",
	fu: "재방문",
};

// MED.001 shared rules — no emoji anywhere; line breaks allowed only where noted.
const EMOJI_RE = /\p{Extended_Pictographic}/u;
const hasEmoji = (v: string) => EMOJI_RE.test(v);
const hasLineBreak = (v: string) => /[\r\n]/.test(v);

export const scheduleAddSchema = z
	.object({
		patientValue: z.string().min(1, "validation:schedule.patientRequired"),
		scheduledAt: z.string().optional(),
		// MED.002: Category mandatory, no default value ("" = unselected).
		type: z.string().optional(),
		purpose: z.string().optional(),
		memo: z.string().optional(),
	})
	.superRefine((v, ctx) => {
		// Scheduled Date & Time: required + must be in the FUTURE (MED.004 / FE-19).
		const at = (v.scheduledAt ?? "").trim();
		if (!at)
			ctx.addIssue({
				path: ["scheduledAt"],
				code: z.ZodIssueCode.custom,
				message: "validation:schedule.dateTimeRequired",
			});
		else if (new Date(at.replace(" ", "T")) <= new Date())
			ctx.addIssue({
				path: ["scheduledAt"],
				code: z.ZodIssueCode.custom,
				message: "validation:schedule.dateTimeFuture",
			});

		// Category (type): required, no default; must be a known schedule type.
		if (!v.type || !SCHEDULE_TYPES.includes(v.type as (typeof SCHEDULE_TYPES)[number]))
			ctx.addIssue({
				path: ["type"],
				code: z.ZodIssueCode.custom,
				message: "validation:common.required",
			});

		// Purpose: required 1..100, no emoji, no line breaks.
		const purpose = (v.purpose ?? "").trim();
		if (purpose.length < 1)
			ctx.addIssue({
				path: ["purpose"],
				code: z.ZodIssueCode.custom,
				message: "validation:schedule.purposeRequired",
			});
		else {
			if (purpose.length > 100)
				ctx.addIssue({
					path: ["purpose"],
					code: z.ZodIssueCode.custom,
					message: "validation:common.maxLength",
				});
			if (hasEmoji(purpose))
				ctx.addIssue({
					path: ["purpose"],
					code: z.ZodIssueCode.custom,
					message: "validation:common.noEmoji",
				});
			if (hasLineBreak(purpose))
				ctx.addIssue({
					path: ["purpose"],
					code: z.ZodIssueCode.custom,
					message: "validation:common.noLineBreak",
				});
		}

		// Memo: optional, max 1000, no emoji, line breaks allowed.
		const memo = (v.memo ?? "").trim();
		if (memo) {
			if (memo.length > 1000)
				ctx.addIssue({
					path: ["memo"],
					code: z.ZodIssueCode.custom,
					message: "validation:common.maxLength",
				});
			if (hasEmoji(memo))
				ctx.addIssue({
					path: ["memo"],
					code: z.ZodIssueCode.custom,
					message: "validation:common.noEmoji",
				});
		}
	});

export type ScheduleAddFormValues = z.infer<typeof scheduleAddSchema>;

/** Build tomorrow's datetime in flatpickr-compatible format ("YYYY-MM-DD HH:MM"). */
export function tomorrowDatetime(): string {
	const d = new Date();
	d.setDate(d.getDate() + 1);
	const pad = (n: number) => String(n).padStart(2, "0");
	return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * A stored 예정 일시 rendered in the format this form's picker reads ("YYYY-MM-DD HH:MM").
 *
 * The server sends an ISO local datetime — `2026-09-01T14:00` — while the flatpickr behind 예정 일시 is
 * configured `Y-m-d H:i`. Handing it the ISO string left it unparsed: the field showed the raw `T` form
 * and the calendar fell back to its own default, so the modal displayed one datetime while its picker
 * highlighted another (SHCT-889).
 *
 * Only the display changes. Both spellings are equally valid to the server — the schedule create/edit
 * contract documents `2026-07-01T14:30` and `2026-07-01 14:30` alike — and the space form is already what
 * this form submits whenever the operator touches the picker.
 *
 * A value that does not match the expected shape is returned untouched: reformatting it on a guess would
 * put an invented datetime in front of a clinician. An empty value yields `undefined` so the caller's own
 * fallback still applies.
 */
export function toPickerDatetime(
	stored: string | null | undefined,
): string | undefined {
	if (!stored) return undefined;
	const parts = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/.exec(stored);
	return parts ? `${parts[1]} ${parts[2]}` : stored;
}
